import { h } from "@stencil/core";
/**
 * The 'ch-paginator-pages' component represents the pagination pages for the 'ch-paginator' component.
 */
export class ChPaginatorPages {
    constructor() {
        this.page = 1;
        this.totalPages = 1;
        this.maxSize = 9;
        this.renderFirstLastPages = true;
        this.textDots = "...";
    }
    buttonActive;
    el;
    pageHandler() {
        this.pageChanged.emit({ page: this.page });
    }
    maxSizeHandler() {
        this.validateMaxSize();
    }
    renderFirstLastPagesHandler() {
        this.validateMaxSize();
    }
    /**
     * Event emitted when the page changes.
     */
    pageChanged;
    componentDidUpdate() {
        if (document.activeElement === this.el) {
            this.buttonActive.focus();
        }
    }
    keyDownHandler(eventInfo) {
        switch (eventInfo.key) {
            case "ArrowLeft":
                this.page = Math.max(this.page - 1, 1);
                break;
            case "ArrowRight":
                this.page = Math.min(this.page + 1, this.totalPages);
                break;
        }
    }
    focusHandler() {
        this.buttonActive.focus();
    }
    clickHandler = (eventInfo) => {
        const button = eventInfo.target;
        this.page = parseInt(button.value);
    };
    getItems() {
        const padLeft = Math.ceil((this.maxSize - 1) / 2);
        const padRight = Math.floor((this.maxSize - 1) / 2);
        let fillLeft, fillStart;
        let fillRight, fillEnd;
        if (this.maxSize === 0 || this.maxSize >= this.totalPages) {
            fillStart = this.fillStart(false);
            fillLeft = this.page - 1;
            fillRight = this.totalPages - this.page;
            fillEnd = this.fillEnd(false);
        }
        else if (this.page <= padLeft && this.page < this.totalPages - padRight) {
            fillStart = this.fillStart(false);
            fillLeft = this.page - 1;
            fillEnd = this.fillEnd(true);
            fillRight = padRight - fillEnd.length + (padLeft - fillLeft);
        }
        else if (this.page > padLeft && this.page < this.totalPages - padRight) {
            fillStart = this.fillStart(true);
            fillLeft = padLeft - fillStart.length;
            fillEnd = this.fillEnd(true);
            fillRight = padRight - fillEnd.length;
        }
        else if (this.page > padLeft && this.page >= this.totalPages - padRight) {
            fillEnd = this.fillEnd(false);
            fillRight = this.totalPages - this.page;
            fillStart = this.fillStart(true);
            fillLeft = padLeft - fillStart.length + padRight - fillRight;
        }
        const items = fillStart
            .concat(this.getRangeItems(this.page - fillLeft, this.page - 1))
            .concat([this.page])
            .concat(this.getRangeItems(this.page + 1, this.page + fillRight))
            .concat(fillEnd);
        return {
            items,
            activeIndex: fillStart.length + fillLeft
        };
    }
    fillStart(render) {
        if (render) {
            return this.renderFirstLastPages ? [1, this.textDots] : [this.textDots];
        }
        else {
            return [];
        }
    }
    fillEnd(render) {
        if (render) {
            return this.renderFirstLastPages
                ? [this.textDots, this.totalPages]
                : [this.textDots];
        }
        else {
            return [];
        }
    }
    getRangeItems(start, end) {
        return Array.from({ length: end - start + 1 }, (_, i) => start + i);
    }
    validateMaxSize() {
        if (this.maxSize > 0) {
            if (!this.renderFirstLastPages && this.maxSize < 3) {
                this.maxSize = 3;
            }
            else if (this.renderFirstLastPages && this.maxSize < 5) {
                this.maxSize = 5;
            }
        }
    }
    render() {
        const { items, activeIndex } = this.getItems();
        return (h("ol", { part: "pages", class: "pages" }, items.map((item, i) => {
            if (typeof item === "number") {
                return (h("li", null, h("button", { part: `page button ${i === activeIndex ? "active" : ""}`, value: item, onClick: this.clickHandler, ref: el => (this.buttonActive =
                        i === activeIndex ? el : this.buttonActive) }, item)));
            }
            else {
                return (h("li", null, h("button", { part: "page button dots", disabled: true }, item)));
            }
        })));
    }
    static get is() { return "ch-paginator-pages"; }
    static get encapsulation() { return "shadow"; }
    static get originalStyleUrls() {
        return {
            "$": ["ch-paginator-pages.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["ch-paginator-pages.css"]
        };
    }
    static get properties() {
        return {
            "page": {
                "type": "number",
                "mutable": true,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The active page number."
                },
                "attribute": "page",
                "reflect": true,
                "defaultValue": "1"
            },
            "totalPages": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "1",
                    "resolved": "1",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The total number of pages."
                },
                "attribute": "total-pages",
                "reflect": true,
                "defaultValue": "1"
            },
            "maxSize": {
                "type": "number",
                "mutable": true,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The maximum number of items to display in the pagination."
                },
                "attribute": "max-size",
                "reflect": true,
                "defaultValue": "9"
            },
            "renderFirstLastPages": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Flag to render the first and last pages."
                },
                "attribute": "render-first-last-pages",
                "reflect": false,
                "defaultValue": "true"
            },
            "textDots": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The text to display for the dots."
                },
                "attribute": "text-dots",
                "reflect": false,
                "defaultValue": "\"...\""
            }
        };
    }
    static get events() {
        return [{
                "method": "pageChanged",
                "name": "pageChanged",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Event emitted when the page changes."
                },
                "complexType": {
                    "original": "ChPaginatorPagesPageChangedEvent",
                    "resolved": "ChPaginatorPagesPageChangedEvent",
                    "references": {
                        "ChPaginatorPagesPageChangedEvent": {
                            "location": "local",
                            "path": "/home/runner/work/chameleon-controls-library/chameleon-controls-library/src/components/paginator/paginator-pages/ch-paginator-pages.tsx",
                            "id": "src/components/paginator/paginator-pages/ch-paginator-pages.tsx::ChPaginatorPagesPageChangedEvent"
                        }
                    }
                }
            }];
    }
    static get elementRef() { return "el"; }
    static get watchers() {
        return [{
                "propName": "page",
                "methodName": "pageHandler"
            }, {
                "propName": "maxSize",
                "methodName": "maxSizeHandler"
            }, {
                "propName": "renderFirstLastPages",
                "methodName": "renderFirstLastPagesHandler"
            }];
    }
    static get listeners() {
        return [{
                "name": "keydown",
                "method": "keyDownHandler",
                "target": undefined,
                "capture": false,
                "passive": true
            }, {
                "name": "focusin",
                "method": "focusHandler",
                "target": undefined,
                "capture": false,
                "passive": true
            }];
    }
}
//# sourceMappingURL=ch-paginator-pages.js.map
